import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Skeleton,
} from "@jem-open/jem-ui";
import { ArrowLeft, CheckCircle2, Settings, X } from "lucide-react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import PageContainer from "../../../themes/components/PageContainer";
import useSiteConfiguration from "../hooks/useSiteConfiguration";
import { useSiteAuditLogApiService } from "../services/api/SiteConfigurationApiService";
import SiteConfigCard from "../components/SiteConfigCard";
import RosterConfigSection from "../components/RosterConfigSection";
import RosterAccordionSummary from "../components/RosterAccordionSummary";
import MoveRosterDialog from "../components/MoveRosterDialog";
import GeofenceRevertConfirmDialog from "../components/GeofenceRevertConfirmDialog";
import AuditHistoryDialog from "../components/AuditHistoryDialog";

interface SiteConfigurationPageInnerProps {
  siteId: string;
}

const SiteConfigurationPageInner: React.FC<SiteConfigurationPageInnerProps> = ({
  siteId,
}) => {
  const navigate = useNavigate();
  const {
    notFound,
    isLoading,
    isError,
    site,
    rosters,
    siteDirty,
    postSaveAlert,
    setPostSaveAlert,
    auditDrawerOpen,
    setAuditDrawerOpen,
    pendingRevertId,
    setPendingRevertId,
    pendingMoveRosterId,
    pendingMoveDestination,
    setPendingMoveDestination,
    movedAlert,
    setMovedAlert,
    derivedRequiredHeadcount,
    rostersInFallback,
    movingRoster,
    moveDestinationSites,
    handleSiteChange,
    discardSiteChanges,
    saveSiteChanges,
    handleRosterChange,
    handleRevertField,
    handleToggleGeofence,
    confirmRevertGeofence,
    saveRoster,
    discardRoster,
    openMoveDialog,
    closeMoveDialog,
    confirmMove,
  } = useSiteConfiguration(siteId);

  const auditLogQuery = useSiteAuditLogApiService(siteId, {
    enabled: auditDrawerOpen,
  });

  if (notFound) {
    return <Navigate to="/site-configuration" replace />;
  }

  if (isLoading) {
    return (
      <PageContainer>
        <div className="space-y-md max-w-[1100px] pt-md">
          <Skeleton className="h-40 w-full rounded-lg" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer>
        <div className="max-w-[1100px] pt-md">
          <Alert variant="error">
            <AlertTitle>Couldn&apos;t load this Site</AlertTitle>
            <AlertDescription>
              <p>
                Something went wrong loading the Site configuration. Please try
                again.
              </p>
            </AlertDescription>
          </Alert>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div
        className="-mx-[40px] -mt-[32px] px-[40px] py-lg mb-md"
        style={{ backgroundColor: "rgba(237,244,251,0.5)" }}
      >
        <Button
          variant="ghost"
          size="sm"
          className="mb-xs -ml-xs"
          leftIcon={<ArrowLeft size={14} />}
          onClick={() => navigate("/site-configuration")}
        >
          Back to Sites &amp; Regions
        </Button>
        <h1 className="font-heading text-2xl font-semibold leading-36 text-greyscale-text-title">
          Site Configuration
        </h1>
        <p className="mt-xxs text-sm text-greyscale-text-body max-w-[760px]">
          Each Roster on this Site uses these settings unless it has its own
          custom value. Site changes update every Roster that doesn&apos;t have
          a custom value.
        </p>
      </div>

      <div className="space-y-md max-w-[1100px] pb-[120px]">
        {/* Roster moved success Alert */}
        {movedAlert && (
          <Alert variant="success" className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-xs top-xs h-6 w-6"
              aria-label="Dismiss"
              onClick={() => setMovedAlert(null)}
            >
              <X size={14} />
            </Button>
            <AlertTitle icon={<CheckCircle2 size={16} />}>
              Roster moved
            </AlertTitle>
            <AlertDescription>
              <p className="pr-lg">
                <strong>{movedAlert.rosterName}</strong> moved to{" "}
                <strong>{movedAlert.destination}</strong>. All settings reset to{" "}
                {movedAlert.destination}&apos;s defaults — review and customise
                the Roster at its new Site as needed.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {/* Post-save result Alert */}
        {postSaveAlert && (
          <Alert variant="success" className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-xs top-xs h-6 w-6"
              aria-label="Dismiss"
              onClick={() => setPostSaveAlert(null)}
            >
              <X size={14} />
            </Button>
            <AlertTitle icon={<CheckCircle2 size={16} />}>
              {postSaveAlert.field} saved
            </AlertTitle>
            <AlertDescription>
              <p className="mb-xs pr-lg">
                Changed from <strong>{postSaveAlert.oldValue}</strong> to{" "}
                <strong>{postSaveAlert.newValue}</strong>. Updated{" "}
                <strong>{postSaveAlert.updated.length}</strong> Roster
                {postSaveAlert.updated.length === 1 ? "" : "s"};{" "}
                <strong>{postSaveAlert.skipped.length}</strong> kept their
                custom value.
              </p>
              {postSaveAlert.updated.length > 0 && (
                <p className="text-xs">
                  <span className="font-semibold">Updated:</span>{" "}
                  {postSaveAlert.updated.join(", ")}.
                </p>
              )}
              {postSaveAlert.skipped.length > 0 && (
                <p className="text-xs">
                  <span className="font-semibold">Kept custom:</span>{" "}
                  {postSaveAlert.skipped.join(", ")}.
                </p>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* ---------- Site card with embedded Save bar ---------- */}
        <SiteConfigCard
          siteName={site.siteName}
          latitude={site.latitude}
          longitude={site.longitude}
          geofenceRadius={site.geofenceRadius}
          lateArrivalThreshold={site.lateArrivalThreshold}
          forceEditing={siteDirty}
          onChange={handleSiteChange}
          onOpenHistory={() => setAuditDrawerOpen(true)}
          onSave={saveSiteChanges}
          onDiscard={discardSiteChanges}
        />

        {/* ---------- Rosters ---------- */}
        <section
          aria-label="Rosters"
          className="rounded-lg border border-greyscale-border bg-white"
        >
          <div className="flex flex-wrap items-center justify-between gap-sm p-md border-b border-greyscale-border">
            <div className="flex items-center gap-xs">
              <div className="rounded-md bg-secondary-pink-100 p-xxs">
                <Settings size={20} className="text-secondary-pink-900" />
              </div>
              <div>
                <h2 className="font-heading text-lg font-semibold text-greyscale-text-title">
                  Rosters on {site.siteName}
                </h2>
                <p className="text-xs text-greyscale-text-caption">
                  {rosters.length} Roster{rosters.length === 1 ? "" : "s"} ·{" "}
                  <strong>{derivedRequiredHeadcount}</strong> expected on site
                  {rostersInFallback > 0 && (
                    <>
                      {" "}
                      ·{" "}
                      <span className="text-greyscale-text-caption">
                        {rostersInFallback} using assigned headcount as fallback
                      </span>
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>

          <Accordion type="multiple" className="px-md">
            {rosters.map((roster) => (
              <AccordionItem key={roster.id} value={roster.id}>
                {/*
                  a11y fix (spec drift #13): the Move Roster control is rendered
                  as a real Button sibling OUTSIDE the AccordionTrigger, not a
                  <span role="button"> nested inside it.
                */}
                <div className="flex items-center">
                  <AccordionTrigger className="flex-1">
                    <RosterAccordionSummary roster={roster} />
                  </AccordionTrigger>
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-sm mr-md"
                    aria-label={`Move ${roster.name} to another Site`}
                    onClick={() => openMoveDialog(roster.id)}
                  >
                    Move Roster
                  </Button>
                </div>
                <AccordionContent>
                  <RosterConfigSection
                    roster={roster}
                    siteDefaults={site}
                    onChange={handleRosterChange}
                    onRevertField={handleRevertField}
                    onToggleGeofence={handleToggleGeofence}
                    onSave={saveRoster}
                    onDiscard={discardRoster}
                  />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </div>

      <MoveRosterDialog
        open={pendingMoveRosterId !== null}
        onOpenChange={(open) => {
          if (!open) closeMoveDialog();
        }}
        rosterName={movingRoster?.name ?? null}
        sourceSiteName={site.siteName}
        destinationSites={moveDestinationSites}
        destination={pendingMoveDestination}
        onDestinationChange={setPendingMoveDestination}
        onConfirm={confirmMove}
      />

      <GeofenceRevertConfirmDialog
        open={pendingRevertId !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRevertId(null);
        }}
        siteName={site.siteName}
        onConfirm={confirmRevertGeofence}
      />

      <AuditHistoryDialog
        open={auditDrawerOpen}
        onOpenChange={setAuditDrawerOpen}
        siteName={site.siteName}
        entries={auditLogQuery.data ?? []}
      />
    </PageContainer>
  );
};

const SiteConfigurationPage: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>();
  if (!siteId) {
    return <Navigate to="/site-configuration" replace />;
  }
  // Key by siteId so all page state resets when navigating between Sites.
  return <SiteConfigurationPageInner key={siteId} siteId={siteId} />;
};

export default SiteConfigurationPage;
